import React, { useCallback, useEffect, useMemo, useState } from "react";
import Spinner from "../components/Spinner/Spinner";
import { formatDateWithDay } from "../utils/dateUtils";
import {
  formatPhoneNumber,
  formatPhoneNumberForSms,
} from "../utils/phoneNumberUtils";
import { activeShuttleParticipants } from "../utils/shuttleParticipants";
import RichTextContent from "../components/Elements/RichTextContent";
import PickupGuideModal from "../components/Driver/PickupGuideModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsers } from "@fortawesome/free-solid-svg-icons";

const assignmentStatuses = [
  { value: "assigned", label: "Assigned" },
  { value: "on_route_to_pickup", label: "On route" },
  { value: "arrived_at_pickup", label: "Arrived" },
  { value: "departed_pickup", label: "Departed" },
  { value: "arrived_at_event", label: "At event" },
  { value: "completed", label: "Done" },
];

const statusLabels = {
  not_checked_in: "Not checked in",
  waiting_at_pickup: "Waiting",
  picked_up: "Picked up",
  no_show: "No-show",
};

const mapLinkForAddress = (address) =>
  address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
    : "#";

const studentStatusPriority = {
  waiting_at_pickup: 0,
  running_late: 1,
  not_checked_in: 2,
  picked_up: 3,
  no_show: 4,
};

const sortStudentsForPickup = (students = []) =>
  [...students].sort((a, b) => {
    const priorityA = studentStatusPriority[a.pickupCheckInStatus] ?? 9;
    const priorityB = studentStatusPriority[b.pickupCheckInStatus] ?? 9;
    if (priorityA !== priorityB) return priorityA - priorityB;
    return String(a.name || "").localeCompare(String(b.name || ""));
  });

const DriverShuttlePage = ({ uniqueCode }) => {
  const apiBaseUrl = process.env.REACT_APP_API_URL;
  const locationSharingStorageKey = `driver-shuttle-location-sharing-${uniqueCode}`;
  const [data, setData] = useState(null);
  const [vehicleDescription, setVehicleDescription] = useState("");
  const [isVehicleDetailsOpen, setIsVehicleDetailsOpen] = useState(false);
  const [locationSharingEnabled, setLocationSharingEnabled] = useState(
    () => localStorage.getItem(locationSharingStorageKey) === "true",
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [showManualEtaFallback, setShowManualEtaFallback] = useState(false);
  const [showPickupGuide, setShowPickupGuide] = useState(false);

  const request = useCallback(
    async (path, options = {}) => {
      const response = await fetch(`${apiBaseUrl}${path}`, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...(options.headers || {}),
        },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.message || "Shuttle request failed.");
      }
      return payload;
    },
    [apiBaseUrl],
  );

  const refresh = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setIsLoading(true);
      setError("");
      const payload = await request(`/api/driver-shuttle/${uniqueCode}`);
      setData(payload);
      if (!silent) setVehicleDescription(payload.shuttle?.vehicleDescription || "");
    } catch (err) {
      setError(err.message || "Unable to load shuttle page.");
    } finally {
      setIsLoading(false);
    }
  }, [request, uniqueCode]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (
      data?.driver?.showPickupPageOnboarding &&
      !data?.driver?.pickupPageOnboardingViewedAt
    ) {
      setShowPickupGuide(true);
    }
  }, [data?.driver?.pickupPageOnboardingViewedAt, data?.driver?.showPickupPageOnboarding]);

  const closePickupGuide = async () => {
    setShowPickupGuide(false);
    if (!data?.driver?.pickupPageOnboardingViewedAt) {
      try {
        await request(`/api/event-participations/pickup-page-onboarding/${uniqueCode}`, {
          method: "PUT",
          body: JSON.stringify({}),
        });
        setData((current) => ({
          ...current,
          driver: {
            ...current.driver,
            pickupPageOnboardingViewedAt: new Date().toISOString(),
          },
        }));
      } catch (err) {
        setError(err.message || "Could not save guide progress.");
      }
    }
  };

  useEffect(() => {
    const poll = () => {
      if (document.visibilityState === "visible") refresh({ silent: true });
    };
    const interval = setInterval(poll, 30000);
    document.addEventListener("visibilitychange", poll);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", poll);
    };
  }, [refresh]);

  const assignments = useMemo(
    () =>
      (data?.assignments || []).map((assignment) => ({
        ...assignment,
        students: activeShuttleParticipants(assignment.students),
      })),
    [data],
  );
  const activeOnRoute = useMemo(
    () =>
      assignments.some(
        (assignment) => assignment.status === "on_route_to_pickup",
      ),
    [assignments],
  );

  const saveVehicle = async (e) => {
    e.preventDefault();
    try {
      setIsSaving(true);
      setNotice("");
      const payload = await request(
        `/api/driver-shuttle/${uniqueCode}/vehicle`,
        {
          method: "PUT",
          body: JSON.stringify({
            vehicleDescription,
          }),
        },
      );
      setData(payload);
      if (vehicleDescription.trim()) {
        setIsVehicleDetailsOpen(false);
      }
      setNotice("Vehicle details saved.");
    } catch (err) {
      setError(err.message || "Could not save vehicle details.");
    } finally {
      setIsSaving(false);
    }
  };

  const shareLocation = useCallback(
    ({ silent = false } = {}) => {
      if (!navigator.geolocation) {
        setIsSaving(false);
        setShowManualEtaFallback(true);
        if (!silent) {
          setError(
            "Location sharing is not available on this device. You can enter ETA manually on each pickup spot.",
          );
          setLocationSharingEnabled(false);
          localStorage.removeItem(locationSharingStorageKey);
        }
        return;
      }
      if (!silent) {
        setIsSaving(true);
        setNotice("");
      }
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const payload = await request(
              `/api/driver-shuttle/${uniqueCode}/location`,
              {
                method: "POST",
                body: JSON.stringify({
                  latitude: position.coords.latitude,
                  longitude: position.coords.longitude,
                }),
              },
            );
            setData(payload);
            setShowManualEtaFallback(false);
            if (!silent) setNotice("Location shared with admins.");
          } catch (err) {
            if (!silent) setError(err.message || "Could not share location.");
          } finally {
            if (!silent) setIsSaving(false);
          }
        },
        () => {
          setIsSaving(false);
          setLocationSharingEnabled(false);
          localStorage.removeItem(locationSharingStorageKey);
          setShowManualEtaFallback(true);
          if (!silent) {
            setError(
              "Location permission was denied. You can enter ETA manually on each pickup spot.",
            );
          }
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
      );
    },
    [locationSharingStorageKey, request, uniqueCode],
  );

  useEffect(() => {
    if (!locationSharingEnabled) return undefined;

    shareLocation({ silent: true });

    if (!activeOnRoute) return undefined;

    const interval = setInterval(() => {
      shareLocation({ silent: true });
    }, 60000);

    return () => clearInterval(interval);
  }, [activeOnRoute, locationSharingEnabled, shareLocation]);

  const toggleLocationSharing = () => {
    const nextValue = !locationSharingEnabled;
    setLocationSharingEnabled(nextValue);

    if (nextValue) {
      localStorage.setItem(locationSharingStorageKey, "true");
      shareLocation();
    } else {
      localStorage.removeItem(locationSharingStorageKey);
      setNotice("Location sharing turned off.");
    }
  };

  const updateAssignmentStatus = async (assignmentId, status) => {
    try {
      setIsSaving(true);
      const payload = await request(
        `/api/driver-shuttle/${uniqueCode}/assignments/${assignmentId}/status`,
        {
          method: "PUT",
          body: JSON.stringify({ status }),
        },
      );
      setData(payload);
    } catch (err) {
      setError(err.message || "Could not update shuttle status.");
    } finally {
      setIsSaving(false);
    }
  };

  const updateAssignmentEta = async (assignmentId, etaMinutes) => {
    try {
      setIsSaving(true);
      const payload = await request(
        `/api/driver-shuttle/${uniqueCode}/assignments/${assignmentId}/eta`,
        {
          method: "PUT",
          body: JSON.stringify({ etaMinutes: Number(etaMinutes) }),
        },
      );
      setData(payload);
      setNotice("ETA updated.");
    } catch (err) {
      setError(err.message || "Could not update ETA.");
    } finally {
      setIsSaving(false);
    }
  };

  const updateStudentStatus = async (studentId, pickupCheckInStatus) => {
    try {
      setIsSaving(true);
      const payload = await request(
        `/api/driver-shuttle/${uniqueCode}/participants/${studentId}/status`,
        {
          method: "PUT",
          body: JSON.stringify({ pickupCheckInStatus }),
        },
      );
      setData(payload);
    } catch (err) {
      setError(err.message || "Could not update student status.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <Spinner />;
  if (error && !data) {
    return <div className="p-6 text-center text-red-600">{error}</div>;
  }

  return (
    <div
      className="min-h-screen bg-no-repeat bg-cover bg-center px-3 py-5"
      style={{ backgroundImage: "url('/background.png')" }}
    >
      <div className="mx-auto max-w-3xl space-y-4">
        <div className="rounded-3xl border border-white/60 bg-white/95 p-5 shadow-xl">
          {data.accountLogo && (
            <div className="mb-4 flex justify-center">
              <img
                src={data.accountLogo}
                alt="Account Logo"
                className="max-h-24 object-contain"
              />
            </div>
          )}
          <p className="text-xs font-black uppercase tracking-wide text-[#004a7e]">
            Shuttle driver
          </p>
          <h1 className="mt-1 text-2xl font-black text-gray-900">
            Hi, {data.driver?.name}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage pickup spots, student pickup status, and your shuttle
            details.
          </p>
        </div>

        {error && (
          <div className="rounded-xl bg-red-50 p-3 text-sm font-bold text-red-700">
            {error}
          </div>
        )}
        {notice && (
          <div className="rounded-xl bg-green-50 p-3 text-sm font-bold text-green-700">
            {notice}
          </div>
        )}

        {data.isEventExpired ? (
          <div className="rounded-2xl bg-white p-6 text-center font-bold text-red-600 shadow-lg">
            This event has ended.
          </div>
        ) : (
          <>
            <div className="rounded-2xl bg-white p-4 shadow-lg">
              <h2 className="text-xl font-black text-gray-900">
                {data.event?.title}
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                {formatDateWithDay(data.event?.dateOfOccurrence)}
              </p>
              {data.event?.location && (
                <a
                  data-tour="view-map"
                  className="mt-1 block text-sm font-semibold text-[#004a7e] underline"
                  href={mapLinkForAddress(data.event.location)}
                  target="_blank"
                  rel="noreferrer"
                >
                  Destination: {data.event.location}
                </a>
              )}
            </div>

            {data.event?.eventMessageForDrivers && (
              <div className="rounded-2xl border-l-4 border-l-amber-500 bg-amber-50 p-4 text-sm text-amber-950 shadow-lg">
                <p className="font-black uppercase tracking-wide text-amber-900">
                  Event Notice
                </p>
                <RichTextContent html={data.event.eventMessageForDrivers} className="mt-1" />
              </div>
            )}

            {data.driver?.messageFromAdmin && (
              <div className="rounded-2xl border-l-4 border-l-[#004a7e] bg-blue-50 p-4 text-sm text-blue-950 shadow-lg">
                <p className="font-black uppercase tracking-wide text-[#004a7e]">
                  Message from admin
                </p>
                <p className="mt-1 whitespace-pre-wrap">{data.driver.messageFromAdmin}</p>
              </div>
            )}

            <form
              onSubmit={saveVehicle}
              className="rounded-2xl bg-white p-4 shadow-lg"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-black text-gray-900">Vehicle details</h3>
                  <p className="mt-1 text-xs text-gray-500">
                    Students will see this vehicle description. Example: Grey
                    Honda Civic.
                  </p>
                  {data.shuttle?.vehicleDescription &&
                    !isVehicleDetailsOpen && (
                      <p className="mt-2 rounded-xl bg-gray-50 px-3 py-2 text-sm font-bold text-gray-900">
                        {data.shuttle.vehicleDescription}
                      </p>
                    )}
                </div>
                {data.shuttle?.vehicleDescription && (
                  <button
                    type="button"
                    className="rounded-lg border border-gray-300 px-3 py-1 text-xs font-bold text-gray-700"
                    onClick={() =>
                      setIsVehicleDetailsOpen(!isVehicleDetailsOpen)
                    }
                  >
                    {isVehicleDetailsOpen ? "Close" : "Edit"}
                  </button>
                )}
              </div>

              {(!data.shuttle?.vehicleDescription || isVehicleDetailsOpen) && (
                <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_auto]">
                  <input
                    className="rounded-xl border border-gray-300 px-3 py-2 text-sm"
                    placeholder="Grey Honda Civic"
                    value={vehicleDescription}
                    onChange={(e) => setVehicleDescription(e.target.value)}
                  />
                  <button
                    className="rounded-xl bg-[#004a7e] px-4 py-2 text-sm font-bold text-white"
                    disabled={isSaving}
                  >
                    Save
                  </button>
                </div>
              )}

              <button
                data-tour="location-sharing"
                type="button"
                className={`mt-3 w-full rounded-xl px-4 py-2 text-sm font-bold ${
                  locationSharingEnabled
                    ? "bg-green-600 text-white"
                    : "border border-[#004a7e] text-[#004a7e]"
                }`}
                onClick={toggleLocationSharing}
                disabled={isSaving}
              >
                {locationSharingEnabled
                  ? "Location sharing on"
                  : "Turn on location sharing"}
              </button>
              {data.shuttle?.lastLocationSharedAt && (
                <p className="mt-2 text-xs text-gray-500">
                  Last location shared:{" "}
                  {new Date(data.shuttle.lastLocationSharedAt).toLocaleString()}
                </p>
              )}
              {activeOnRoute && locationSharingEnabled && (
                <p className="mt-2 text-xs font-semibold text-green-700">
                  ETA refreshes every minute while you are on route.
                </p>
              )}
              {locationSharingEnabled && !activeOnRoute && (
                <p className="mt-2 text-xs font-semibold text-green-700">
                  Location sharing will resume ETA refresh when you are on
                  route.
                </p>
              )}
            </form>

            {assignments.length === 0 && (
              <div className="rounded-2xl bg-white p-6 text-center text-gray-600 shadow-lg">
                No pickup spots have been assigned to you yet.
              </div>
            )}

            {assignments.map((assignment) => (
              <PickupSpotCard
                key={assignment.id}
                assignment={assignment}
                isSaving={isSaving}
                showManualEtaFallback={showManualEtaFallback}
                onStatusChange={updateAssignmentStatus}
                onEtaChange={updateAssignmentEta}
                onStudentStatusChange={updateStudentStatus}
              />
            ))}
          </>
        )}
      </div>
      <PickupGuideModal
        isOpen={showPickupGuide}
        onClose={closePickupGuide}
        mode="shuttle"
      />
    </div>
  );
};

const PickupSpotCard = ({
  assignment,
  isSaving,
  showManualEtaFallback,
  onStatusChange,
  onEtaChange,
  onStudentStatusChange,
}) => {
  const [manualEtaMinutes, setManualEtaMinutes] = useState(
    assignment.etaMinutes || "",
  );
  const pickedUp = assignment.students
    .filter((student) => student.pickupCheckInStatus === "picked_up")
    .reduce((sum, student) => sum + (student.noOfPpl || 1), 0);
  const sortedStudents = sortStudentsForPickup(assignment.students);
  const waitingPeople = assignment.students
    .filter((student) => student.pickupCheckInStatus === "waiting_at_pickup")
    .reduce((sum, student) => sum + (student.noOfPpl || 1), 0);

  return (
    <div data-tour="pickup-card" className="overflow-hidden rounded-2xl bg-white shadow-lg">
      <div className="border-b border-gray-100 bg-gray-50 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-black uppercase tracking-wide text-[#004a7e]">
              Pickup spot
            </p>
            <h3 className="mt-1 text-xl font-black text-gray-900">
              {assignment.pickupSpot?.name}
            </h3>
            {assignment.pickupSpot?.address ? (
              <a
                className="mt-2 block text-sm font-bold text-[#004a7e] underline decoration-[#004a7e]/30 underline-offset-2"
                href={mapLinkForAddress(assignment.pickupSpot.address)}
                target="_blank"
                rel="noreferrer"
              >
                {assignment.pickupSpot.address}
              </a>
            ) : (
              <p className="mt-1 text-sm text-gray-600">No address set</p>
            )}
            {assignment.pickupSpot?.pickupTime && (
              <div data-tour="pickup-time" className="mt-3 inline-flex items-center rounded-2xl bg-[#004a7e] px-4 py-2 text-white shadow-sm">
                <span className="text-[10px] font-black uppercase tracking-wide text-white/75">
                  Pickup time
                </span>
                <span className="ml-2 text-lg font-black">
                  {assignment.pickupSpot.pickupTime}
                </span>
              </div>
            )}
            {assignment.etaMinutes && (
              <p className="mt-1 text-xs font-bold text-green-700">
                ETA to pickup: {assignment.etaMinutes} min
                {assignment.etaDistanceMiles
                  ? ` · ${assignment.etaDistanceMiles} mi`
                  : ""}
                {assignment.etaSource === "manual"
                  ? " · entered by driver"
                  : ""}
              </p>
            )}
            <p className="mt-1 text-xs font-bold text-gray-700">
              Expected pickups here: {assignment.expectedRiders ?? assignment.students.length}
            </p>
          </div>
          <div className="rounded-xl bg-white px-3 py-2 text-center shadow-sm">
            <p className="text-lg font-black text-gray-900">
              {pickedUp}/{assignment.capacity || assignment.students.length}
            </p>
            <p className="text-[10px] font-bold uppercase text-gray-500">
              Picked up / capacity
            </p>
          </div>
        </div>
      </div>

      <div className="p-4">
        <div data-tour="status-steps" className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {assignmentStatuses.map((status) => (
            <button
              key={status.value}
              type="button"
              disabled={isSaving}
              onClick={() => onStatusChange(assignment.id, status.value)}
              className={`rounded-xl px-2 py-2 text-xs font-black ${
                assignment.status === status.value
                  ? "bg-[#004a7e] text-white"
                  : "bg-gray-100 text-gray-700"
              }`}
            >
              {status.label}
            </button>
          ))}
        </div>

        {showManualEtaFallback && (
          <form
            className="mt-3 rounded-xl bg-amber-50 p-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (!manualEtaMinutes) return;
              onEtaChange(assignment.id, manualEtaMinutes);
            }}
          >
            <p className="text-xs font-bold text-amber-900">
              Location sharing failed. Enter ETA manually.
            </p>
            <div className="mt-2 flex gap-2">
              <input
                className="min-w-0 flex-1 rounded-lg border border-amber-200 px-3 py-2 text-sm"
                inputMode="numeric"
                min="1"
                max="999"
                placeholder="Minutes"
                value={manualEtaMinutes}
                onChange={(e) => setManualEtaMinutes(e.target.value)}
              />
              <button
                type="submit"
                disabled={!manualEtaMinutes}
                className="rounded-lg bg-amber-600 px-3 py-2 text-sm font-black text-white disabled:bg-gray-300"
              >
                Save ETA
              </button>
            </div>
          </form>
        )}

        <div className="mt-4 space-y-2">
          {assignment.students.length > 0 && (
            <p className="rounded-xl bg-blue-50 px-3 py-2 text-sm font-black text-[#004a7e]">
              {waitingPeople} people waiting at pickup spot
            </p>
          )}
          {assignment.students.length === 0 && (
            <p className="rounded-xl bg-gray-50 p-3 text-sm text-gray-500">
              No students assigned to this pickup spot.
            </p>
          )}
          {sortedStudents.map((student) => (
            <StudentRow
              key={student.eventParticipationId}
              student={student}
              isSaving={isSaving}
              onStudentStatusChange={onStudentStatusChange}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const StudentRow = ({ student, isSaving, onStudentStatusChange }) => {
  const isPickedUp = student.pickupCheckInStatus === "picked_up";
  const isNoShow = student.pickupCheckInStatus === "no_show";
  const rowClass = isPickedUp
    ? "border-green-200 bg-green-50"
    : isNoShow
      ? "border-red-200 bg-red-50"
      : "border-gray-100 bg-white";

  const undo = () =>
    onStudentStatusChange(student.eventParticipationId, "not_checked_in");

  return (
    <div className={`rounded-xl border p-3 ${rowClass}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2 font-black text-gray-900">
            <span className="truncate">{student.name}</span>
            <span className="rounded-full bg-[#eaf3fa] px-2 py-0.5 text-[10px] font-black text-[#004a7e]">
              {student.noOfPpl || 1} <FontAwesomeIcon icon={faUsers} />
            </span>
          </p>
          <a
            className="text-sm font-semibold text-[#004a7e]"
            href={`sms:${formatPhoneNumberForSms(student.phone)}`}
          >
            {formatPhoneNumber(student.phone)}
          </a>
          <p className="mt-1 text-xs text-gray-500">
            {statusLabels[student.pickupCheckInStatus] ||
              student.pickupCheckInStatus}
            {student.runningLateMinutes
              ? ` · running ${student.runningLateMinutes} min late`
              : ""}
          </p>
        </div>

        <div className="flex shrink-0 flex-col gap-1 text-right">
          {isPickedUp || isNoShow ? (
            <button
              type="button"
              disabled={isSaving}
              onClick={undo}
              className="rounded-lg bg-white px-3 py-2 text-xs font-black text-gray-800 shadow-sm"
            >
              Undo
            </button>
          ) : (
            <>
              <button
                type="button"
                disabled={isSaving}
                onClick={() =>
                  onStudentStatusChange(
                    student.eventParticipationId,
                    "picked_up",
                  )
                }
                className="rounded-lg bg-green-600 px-3 py-2 text-xs font-black text-white shadow-sm"
              >
                Mark Picked Up
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriverShuttlePage;
